import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { CheckCircle, Phone, Mail, MapPin, CheckCircle2 } from "lucide-react";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { SearchInput } from "@shared/components/SearchInput";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { BUTTON_FIELD_CLASSES } from "@shared/utils/constants";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { paymentsApi } from "@features/cx/paymentsv2/api";
import { useResolveBulkPaymentRecord } from "@features/cx/paymentsv2/hooks";
import { useToast } from "@shared/hooks/use-toast";

interface ResolveDialogProps {
  isOpen: boolean;
  onClose: () => void;
  rowData: any;
}

export const ResolveDialog = ({
  isOpen,
  onClose,
  rowData,
}: ResolveDialogProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const remoteUtilityId = useRemoteUtilityId();
  const { toast } = useToast();

  const { mutate: resolveRecord, isPending: isResolving } =
    useResolveBulkPaymentRecord();

  const handleSubmit = () => {
    if (!selectedCustomer) {
      toast({
        title: "Consumer Required",
        description: "Please select a consumer to resolve this record.",
        variant: "destructive",
      });
      return;
    }
    resolveRecord(
      { id: rowData.id, consumerId: Number(selectedCustomer.id) },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  const handleCustomerSearch = async (term: string) => {
    if (term.length < 2) return [];
    setSearchTerm(term);
    try {
      const results = await paymentsApi.searchBulkPaymentConsumers({
        search: term,
        remoteUtilityId: Number(remoteUtilityId),
      });

      return (
        results?.map((r: any) => ({
          id: r.id,
          label: r.name || "N/A",
          name: r.name || "N/A",
          accountNumber: r.consumerNo || "N/A",
          phone: r.contactNumber || "N/A",
          email: r.email || "N/A",
          status: r.statusDisplay || "N/A",
        })) || []
      );
    } catch (error) {
      console.error("Failed to search consumers", error);
      return [];
    }
  };

  const handleSelectCustomer = (customer: any) => {
    setSelectedCustomer(customer);
    form.setValue("consumerSearch", customer.name, { shouldValidate: true });
  };

  const renderCustomerResult = (result: any) => (
    <div className="p-3 hover:bg-blue-50 cursor-pointer rounded border-l-4 border-transparent hover:border-blue-400 transition-all text-left">
      <div className="font-medium text-gray-900">{result.name}</div>
      <div className="text-sm text-gray-600">
        Account #{result.accountNumber}
      </div>
    </div>
  );

  const fields: FormField[] = [
    {
      name: "consumerSearch",
      label: "Search Consumer",
      type: "custom" as const,
      required: true,
      customRender: () => (
        <div className="w-full">
          <div className="relative">
            <SearchInput
              placeholder="Search by name, account, or phone"
              onSearch={handleCustomerSearch}
              onSelect={handleSelectCustomer}
              renderResult={renderCustomerResult}
              minSearchLength={2}
              className="w-full bg-white border-gray-300 focus:bg-white"
            />
          </div>

          {selectedCustomer && (
            <div className="mt-4 rounded-lg border border-green-200 overflow-hidden">
              {/* Header */}
              <div className="px-4 py-3 border-b border-green-200 bg-green-50 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-medium text-green-800">
                    {selectedCustomer.name
                      .split(" ")
                      .map((n: string) => n[0])
                      .join("")}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-green-900 truncate">
                      {selectedCustomer.name}
                    </p>
                  </div>
                  <p className="text-xs text-green-700 opacity-75 mt-0.5">
                    {selectedCustomer.accountNumber}
                  </p>
                </div>
                <span className="px-2 py-0.5 rounded-full bg-green-200 text-green-800 text-[10px] font-medium flex-shrink-0">
                  {selectedCustomer.status}
                </span>
              </div>

              {/* Body */}
              <div className="px-4 py-3 bg-green-50/50 grid grid-cols-2 gap-x-4 gap-y-3">
                {/* Phone */}
                <div className="flex items-start gap-2">
                  <Phone className="h-3.5 w-3.5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-wide text-green-700 opacity-70 font-medium">
                      Phone
                    </p>
                    <p className="text-xs text-green-900 break-words">
                      {selectedCustomer.phone}
                    </p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-2">
                  <Mail className="h-3.5 w-3.5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-wide text-green-700 opacity-70 font-medium">
                      Email
                    </p>
                    <p className="text-xs text-green-900 break-all">
                      {selectedCustomer.email}
                    </p>
                  </div>
                </div>

                {/* Address — full width */}
                <div className="col-span-2 flex items-start gap-2">
                  <MapPin className="h-3.5 w-3.5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-wide text-green-700 opacity-70 font-medium">
                      Address
                    </p>
                    <p className="text-xs text-green-900 break-words">
                      {selectedCustomer.address}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ),
    },
  ];

  const form = FormService.createForm({
    fields: fields, // Replaced below
    mode: "onChange",
    defaultValues: {
      consumerSearch: "",
    },
  });

  const detailsData = [
    { label: "Transaction Ref", value: rowData?.txnRef },
    { label: "Amount", value: rowData?.amount },
    { label: "Mode", value: rowData?.mode },
    { label: "Payment Date", value: rowData?.valueDate },
    {
      label: "Flagged As",
      value: <span className="text-amber-600">{rowData?.status}</span>,
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg p-0 bg-white overflow-visible">
        <DialogHeader className="p-4 border-b bg-gray-50 flex flex-row items-center justify-between rounded-t-lg">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-gray-700" />
            <DialogTitle className="text-lg font-bold text-gray-900">
              Resolve Unmatched Payment
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="p-6 space-y-6">
          {/* Top Info Section */}
          <div className="space-y-3">
            {detailsData.map((item, index) => (
              <ValueLabel
                key={index}
                label={item.label}
                value={item.value}
                layout="horizontal"
                className="justify-between w-full [&_p]:text-sm"
              />
            ))}
          </div>

          {/* Search for consumer manually using DynamicForm */}
          <div>
            <DynamicForm
              fields={fields}
              form={form}
              gridColumns={1}
              responsiveColumns={{ sm: 1, md: 1 }}
              onSubmit={handleSubmit}
              onReset={onClose}
              config={{
                showSubmitButton: true,
                showResetButton: true,
                submitButtonText: "Post to Consumer",
                resetButtonText: "Cancel",
                classes: BUTTON_FIELD_CLASSES,
              }}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
